import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine,
} from "recharts";
import { createFormatter, type Jurisdiction } from "@/lib/formatters";
import type { PolicyLoanLedgerRow, StandardLoanAmortizationRow } from "@/lib/calculationEngine";
import { buildLumpSumBehavioralCurve } from "@/lib/chartCurveBuilder";
import { numericField } from "@/lib/chartTypes";

interface LumpSumChartProps {
  originalPolicyLedger: PolicyLoanLedgerRow[];
  lumpSumPolicyLedger: PolicyLoanLedgerRow[];
  standardAmortization: StandardLoanAmortizationRow[];
  jurisdiction: Jurisdiction;
}

export function LumpSumChart({
  originalPolicyLedger,
  lumpSumPolicyLedger,
  standardAmortization,
  jurisdiction,
}: LumpSumChartProps) {
  const { fmt } = createFormatter(jurisdiction);

  const data = useMemo(() => {
    return buildLumpSumBehavioralCurve(originalPolicyLedger, lumpSumPolicyLedger, standardAmortization);
  }, [originalPolicyLedger, lumpSumPolicyLedger, standardAmortization]);

  const isDark = typeof window !== 'undefined' && document.documentElement.classList.contains('dark');
  const originalColor = isDark ? "hsl(0, 85%, 62%)" : "hsl(0, 72%, 50%)";
  const lumpColor = isDark ? "hsl(210, 90%, 62%)" : "hsl(215, 75%, 48%)";
  const standardColor = isDark ? "hsl(220, 9%, 60%)" : "hsl(220, 9%, 46%)";
  const recaptureColor = isDark ? "hsl(142, 70%, 55%)" : "hsl(142, 68%, 42%)";
  const gridColor = isDark ? "hsl(222, 15%, 20%)" : "hsl(220, 13%, 92%)";
  const tickColor = isDark ? "hsl(220, 9%, 55%)" : "hsl(220, 9%, 46%)";

  // Fixed legend from the chart's own colour values — Recharts' auto legend
  // renders a blank swatch for the gradient-filled Area entries.
  const legendItems: { key: string; color: string; dashed?: boolean }[] = [];
  if (data.some(d => numericField(d, "standard") > 0)) legendItems.push({ key: "Intended Loan", color: standardColor, dashed: true });
  if (data.some(d => numericField(d, "original") > 0)) legendItems.push({ key: "Policy Loan (Original)", color: originalColor });
  if (data.some(d => numericField(d, "withLumpSum") > 0)) legendItems.push({ key: "Policy Loan (With Lump Sum)", color: lumpColor });
  if (data.some(d => numericField(d, "marginRecaptureBalance") > 0)) legendItems.push({ key: "Family Financing Margin™ Growth", color: recaptureColor, dashed: true });

  const fixedLegend = () => (
    <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 pt-2 text-xs">
      {legendItems.map(item => (
        <span key={item.key} className="inline-flex items-center gap-1.5">
          {item.dashed ? (
            <svg width="18" height="4" aria-hidden="true">
              <line x1="0" y1="2" x2="18" y2="2" stroke={item.color} strokeWidth="2.5" strokeDasharray="4 3" />
            </svg>
          ) : (
            <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: item.color }} />
          )}
          <span className="text-muted-foreground">{item.key}</span>
        </span>
      ))}
    </div>
  );

  // Find lump-sum policy payoff milestone
  let lumpPayoffPeriod: number | null = null;
  for (let i = 1; i < data.length; i++) {
    const prev = numericField(data[i - 1], "withLumpSum");
    const cur = numericField(data[i], "withLumpSum");
    if (prev > 0.01 && cur <= 0.01) {
      lumpPayoffPeriod = numericField(data[i], "period");
      break;
    }
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold">
          Lump Sum Impact — Balance Trajectory Comparison
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-0.5">
          See how lump sum payments accelerate payoff compared to the original policy loan and intended loan.
        </p>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={420}>
          <ComposedChart data={data} margin={{ top: 10, right: 24, left: 14, bottom: 40 }}>
            <defs>
              <linearGradient id="lumpOriginalGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={originalColor} stopOpacity={0.35} />
                <stop offset="100%" stopColor={originalColor} stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="lumpWithGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={lumpColor} stopOpacity={0.4} />
                <stop offset="100%" stopColor={lumpColor} stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="lumpRecaptureGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={recaptureColor} stopOpacity={0.05} />
                <stop offset="100%" stopColor={recaptureColor} stopOpacity={0.45} />
              </linearGradient>
              <filter id="lumpGlow" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="3" result="blur" />
                <feFlood floodColor={lumpColor} floodOpacity="0.45" result="color" />
                <feComposite in="color" in2="blur" operator="in" result="shadow" />
                <feMerge><feMergeNode in="shadow" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            </defs>

            <CartesianGrid strokeDasharray="3 6" stroke={gridColor} vertical={false} />
            <XAxis
              dataKey="period"
              tick={{ fontSize: 11, fill: tickColor, fontWeight: 500 }}
              interval="preserveStartEnd"
              axisLine={{ stroke: gridColor }}
              tickLine={{ stroke: gridColor }}
              label={{
                value: "Payments",
                position: "insideBottom",
                offset: -8,
                style: { fontSize: 12, fill: tickColor, fontWeight: 600, letterSpacing: '0.05em' },
              }}
            />
            <YAxis
              tickFormatter={(v: number) => fmt(v)}
              tick={{ fontSize: 11, fill: tickColor, fontWeight: 500 }}
              axisLine={false}
              tickLine={false}
              width={80}
            />
            <Tooltip
              formatter={(value: number, name: string) => [fmt(value), name]}
              labelFormatter={(label: number) => `Payment ${label}`}
              contentStyle={{
                fontSize: 12,
                borderRadius: 12,
                backgroundColor: isDark ? 'hsla(222, 22%, 12%, 0.92)' : 'hsla(0, 0%, 100%, 0.95)',
                borderColor: isDark ? 'hsl(222, 15%, 22%)' : 'hsl(220, 13%, 88%)',
              }}
            />
            <Legend content={fixedLegend} />

            {lumpPayoffPeriod != null && (
              <ReferenceLine
                x={lumpPayoffPeriod}
                stroke={recaptureColor}
                strokeDasharray="4 4"
                strokeWidth={1.5}
                label={{
                  value: `Accelerated Payoff • Pmt ${lumpPayoffPeriod}`,
                  position: "insideTopRight",
                  fill: recaptureColor,
                  fontSize: 10,
                  fontWeight: 600,
                }}
              />
            )}

            <Area type="monotone" dataKey="original" name="Policy Loan (Original)" fill="url(#lumpOriginalGrad)" stroke="none" legendType="none" />
            <Area type="monotone" dataKey="withLumpSum" name="Policy Loan (With Lump Sum)" fill="url(#lumpWithGrad)" stroke="none" legendType="none" />
            <Area type="monotone" dataKey="marginRecaptureBalance" name="Family Financing Margin™ Growth" fill="url(#lumpRecaptureGrad)" stroke={recaptureColor} strokeWidth={2.25} strokeDasharray="5 3" connectNulls={false} animationDuration={1400} />

            <Line type="monotone" dataKey="standard" name="Intended Loan" stroke={standardColor} strokeWidth={1.75} strokeDasharray="6 3" dot={false} connectNulls />
            <Line type="monotone" dataKey="original" name="Policy Loan (Original)" stroke={originalColor} strokeWidth={2.5} dot={false} connectNulls animationDuration={1400} />
            <Line type="monotone" dataKey="withLumpSum" name="Policy Loan (With Lump Sum)" stroke={lumpColor} strokeWidth={3} dot={false} filter="url(#lumpGlow)" connectNulls animationDuration={1600} />
          </ComposedChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
